<template>
    <div class="dashboard-profile">
        <v-snackbar
            v-model="snackbar"
            :bottom="true"
            :vertical="true"
            :timeout="3000"
        >
            {{ $t('Profile updated') }}
        </v-snackbar>
        <v-dialog v-if="!info['is_pro']" v-model="plans_comparison"
                  fullscreen hide-overlay transition="dialog-bottom-transition"
        >
            <PlansComparison :info="info"
                             :loading="loading"
                             :show_special_offer_alert="showSpecialOfferAlert"
                             @close="plans_comparison = false"
                             @pay-membership="payMembership"
                             @basic-plan-publish="basicPlanPublish"
            />
        </v-dialog>
        <v-dialog v-model="payment.dialog" max-width="470">
            <v-card>
                <v-card-title class="headline">
                    <v-icon color="success"
                            class="notranslate"
                            left
                            v-if="payment.status === 'paid'"
                    >check_circle</v-icon>
                    <v-icon color="info"
                            class="notranslate"
                            left
                            v-else-if="payment.status === 'waiting_payment'"
                    >info</v-icon>
                    <v-icon color="error"
                            class="notranslate"
                            left
                            v-else
                    >warning</v-icon>
                    {{payment.title}}
                </v-card-title>
                <v-card-text>
                  {{payment.text}}
                  <SpecialOfferAlert v-if="showSpecialOfferAlert" :info="info" style="margin-top: 16px !important;"/>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat
                           v-if="payment.status === 'no_payment'"
                           color="accent"
                           :href="info['permalink']"
                           target="_blank"
                    >{{ $t('Preview profile') }}</v-btn>
                    <v-btn flat
                           v-if="payment.status === 'refused'"
                           color="accent"
                           @click="payMembership"
                    >{{ $t('Try again') }}</v-btn>
                    <v-btn
                           v-else-if="payment.status === 'no_payment'"
                           color="accent"
                           @click="payMembership"
                    >{{ $t('Pay') }}</v-btn>
                    <v-btn flat
                           v-else
                           color="accent"
                           :href="payment.url"
                           target="_blank"
                    >{{payment.status === 'paid'
                        ? $t('View profile')
                        : $t('View bank slip')
                        }}</v-btn>
                    <v-btn flat @click="payment.dialog = false">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-layout row wrap align-center>
            <v-flex v-if="bannerInfo.active" key="profileBanner" grow class="px-1 mt-2">
                <Banner
                    :button-text="bannerInfo.buttonText"
                    :type="bannerInfo.type"
                    :action="bannerInfo.action"
                >{{ bannerInfo.alertText }}</Banner>
            </v-flex>
            <v-flex v-if="showSpecialOfferAlert" key="showSpecialOffer" shrink class="px-1 mt-2">
                <SpecialOfferAlert :info="info"/>
            </v-flex>
            <v-flex v-if="showProPlanOffer" key="proPlanOffer" grow class="px-1 mt-2">
                <v-btn large block
                       depressed
                       round
                       color="#FDD835"
                       @click="showPlansComparison(false)"
                       class="ma-0 px-4"
                >
                    {{ $t(info.status === 'active' ? 'Upgrade to Pro Plan' : 'Publish to Pro Plan') }}
                </v-btn>
            </v-flex>
          <v-flex v-if="showBasicPlanOffer" key="basicPlanOffer" shrink class="px-1 mt-2">
            <v-btn large block
                   depressed
                   round
                   color="#FDD835"
                   @click="showPlansComparison(false)"
                   class="ma-0 px-4"
            >
              {{ $t('Publish to Basic Plan') }}<br>{{ $t('Free') }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-tabs class="profile-tabs"
                v-model="profile_tab"
                color="transparent"
                slider-color="accent"
                fixed-tabs
                light
        >
            <v-tab key="basic" ripple>{{ $t('Basic') }}</v-tab>
            <v-tab key="portfolio" ripple>{{ $t('Portfolio') }}</v-tab>
            <v-tab key="interview" ripple :disabled="!info['is_pro']">{{ $t('Interview') }}</v-tab>
            <v-tab-item key="basic">
                <ProfileBasic :loading="loading" :error="error" :info="info"
                              :offers_basic_plan="offers_basic_plan"
                              :lastPayment="lastPayment"
                              @submit-profile="submitProfile"
                              @show-plans-comparison="showPlansComparison(false)"
                              @pay-membership="payMembership"
                />
            </v-tab-item>
            <v-tab-item key="portfolio">
                <ProfileImages v-if="info.media_type === 'image'" :id="id"
                               @after-submit-alerts="afterSubmitAlerts"
                />
                <ProfileVideos v-else :id="id" @after-submit-alerts="afterSubmitAlerts" />
            </v-tab-item>
            <v-tab-item key="interview">
                    <ProfileInterview :loading="loading" :error="error" :info="info"
                                       @submit-profile="submitProfile"
                   />
            </v-tab-item>
        </v-tabs>
    </div>
</template>

<script>
  import VTabs from 'vuetify/es5/components/VTabs/VTabs'
  import VTab from 'vuetify/es5/components/VTabs/VTab'
  import VTabItem from 'vuetify/es5/components/VTabs/VTabItem'
  import VTextField from 'vuetify/es5/components/VTextField/VTextField'
  import VForm from 'vuetify/es5/components/VForm/VForm'
  import VTooltip from 'vuetify/es5/components/VTooltip/VTooltip'
  import VTextarea from 'vuetify/es5/components/VTextarea/VTextarea'
  import VSelect from 'vuetify/es5/components/VSelect/VSelect'
  import VSnackbar from 'vuetify/es5/components/VSnackbar/VSnackbar'
  import VAlert from 'vuetify/es5/components/VAlert/VAlert'
  import VDialog from 'vuetify/es5/components/VDialog/VDialog'
  import VCard from 'vuetify/es5/components/VCard/VCard'
  import VIcon from 'vuetify/es5/components/VIcon/VIcon'
  import * as api from '../utils/api'
  import ProfileImages from './ProfileImages'
  import {
    UPDATE_SEGMENTS
  } from '../store/types'
  import { brStates, themes } from '../utils/strings'
  import PaymentLoader from './PaymentLoader'
  import paymentMixin from '../utils/payment_mixin'
  import 'moment/locale/pt-br'
  import { formatMoney } from '../utils/functions'
  import ProfileVideos from './ProfileVideos'
  import ProfileBasic from "./ProfileBasic";
  import ProfileInterview from "./ProfileInterview";
  import SpecialOfferAlert from "./SpecialOfferAlert";
  import Banner from "./Banner";
  import {annuityRegularPrice} from "../utils/rules";
  import PlansComparison from "./PlansComparison";
  import * as moment from 'moment'
  export default {
    name: 'Profile',
    mixins: [
      paymentMixin
    ],
    components: {
      PlansComparison,
      Banner,
      SpecialOfferAlert,
      ProfileInterview,
      ProfileBasic,
      VTabs,
      VTab,
      VTabItem,
      ProfileVideos,
      PaymentLoader,
      ProfileImages,
      VTextarea,
      VSelect,
      VTooltip,
      VForm,
      VTextField,
      VSnackbar,
      VAlert,
      VDialog,
      VCard,
      VIcon
    },
    props: {
      id: Number
    },
    data: () => ({
      profile_tab: null,
      payment: {
        dialog: false,
        title: '',
        text: '',
        status: '',
        url: ''
      },
      getAddressData: '',
      valid: false,
      loading: false,
      snackbar: false,
      error: false,
      plans_comparison: false,
      dissmiss_media_alert_action: null,
      offers_basic_plan: null,
      states: brStates,
      themes: themes
    }),
    computed: {
      info: function () {
        return this.$store.state.segments
          .find(el => el.data.id === this.id).data
      },
      lastPayment: function () {
        if (this.$store.state.user.payment_history) {
          return this.$store.state.user.payment_history.slice().reverse().find(el => {
            return (el['finality']['finality'] === 'membership' && el['finality']['post_id'] == this.id)
          })
        } else {
          return {}
        }
      },
      showProPlanOffer: function () {
        if (this.info.is_pro && this.info.status === 'active') {
          return false;
        }
        return (
          !this.info.is_pro &&
          (this.info['status'] === 'file_sent' || this.info['status'] === 'active' || this.info['status'] === 'inactive')
        )
      },
      showBasicPlanOffer: function () {
        return this.offers_basic_plan && this.info['status'] === 'payment_pending'
      },
      showSpecialOfferAlert: function () {
        let regularPrice = this.info.country.iso === 'br'
            ? annuityRegularPrice['br']
            : annuityRegularPrice['ex']
        if (this.info.is_pro && this.info.status === 'active') {
          return false;
        }
        return (
          !(this.info.status === 'active' && !this.offers_basic_plan) &&
          regularPrice > this.info['payment_info']['amount'] &&
          this.info['payment_info']['type'] === 'annually'
        )
      },
      mediaLen: function() {
        return Object.keys(this.info.media).length
      },
      basicFormIsComplete: function () {
        return (
            this.info.name !== '' &&
            this.info.phone !== '' &&
            this.info.website !== '' &&
            this.info.city !== '' &&
            this.info.facebook !== ''
        )
      },
      bannerInfo: function () {
        switch (this.info.status) {
          case 'approved': return {
            active: true,
            type: 'info',
            alertText: this.$t('You have been approved, fill the form and have your profile published!'),
            buttonText: ''
          }
          case 'file_sent': return !this.offers_basic_plan
            ? {
              active: true,
              type: 'warning',
              alertText: this.$t('Make the payment to have your profile published!'),
              buttonText: this.$t('Pay'),
              action: this.payMembership
            }
            : {
              active: true,
              type: 'warning',
              alertText: this.$t('Your profile is ready to be published'),
              buttonText: this.$t('Publish to Basic Plan') + "<br>" + this.$t('Free'),
              action: this.showPlansComparison
            }
          case 'payment_pending': {
            const active = true
            const type = 'warning'
            let alertText, buttonText, action
            if (this.info.country.iso === 'br') {
              if (this.lastPayment.details['payment_method'] === 'credit_card') {
                alertText = (this.lastPayment.status === 'refused'
                    ? this.$t('Your payment was declined by your credit card provider')
                    : this.$t('Your payment is being processed')
                )
                buttonText = (this.lastPayment.status === 'refused'
                    ? this.$t('Pay')
                    : ''
                )
                action = this.payMembership
              } else {
                if (this.validateTime(this.lastPayment.details['boleto_expiration_date'])) {
                  alertText = this.$t('Payment pending, upon confirmation, the profile will be published.')
                  buttonText = this.$t('View bank slip')
                  action = this.lastPayment.details['boleto_url']
                } else {
                  alertText = this.$t('The payment is past due. Use the button next to make a new payment')
                  buttonText = this.$t('Pay')
                  action = this.payMembership
                }
              }
            } else {
              alertText = this.$t('Your payment has not yet been confirmed')
              buttonText = this.$t('Pay')
              action = this.payMembership
            }
            return { active, type, alertText, buttonText, action }
          }
          case 'active': return {
            active: true,
            type: 'success',
            alertText: this.$t('Your profile is publicly active', {plan: this.info.is_pro ? 'Pro' : 'Basic'}),
            buttonText: this.$t('View profile'),
            action: this.info['permalink']
          }
          case 'renewal': return {
            active: true,
            type: 'warning',
            alertText: this.$t('Annuity renewal pending'),
            buttonText: this.$t('Pay'),
            action: this.payMembership
          }
          case 'inactive':
            return !this.offers_basic_plan
              ? {
                active: true,
                type: 'error',
                alertText: this.$t('Your profile is disabled'),
                buttonText: this.$t('Pay'),
                action: this.payMembership
              }
              : {
                active: true,
                type: 'warning',
                alertText: this.$t('Your profile is ready to be published'),
                buttonText: this.$t('Publish to Basic Plan') + "<br>" + this.$t('Free'),
                action: this.showPlansComparison
              }
          default: return {
            active: false,
            type: '',
            alertText: '',
            buttonText: ''
          }
        }
      }
    },
    methods: {
      formatMoney: function (amount) {
        const currency = (this.info.country.iso === 'br') ? 'R$' : 'U$'
        return formatMoney(currency, amount)
      },
      validateTime: function (time) {
        return moment().isBefore(time * 1000)
      },
      showPlansComparison: function (bypass = false) {
        if (this.mediaLen < 3 && !bypass) {
          this.dissmiss_media_alert_action = 'show_plans_comparison'
        } else {
          this.plans_comparison = true
        }
      },
      initPaymentDialog: function() {
        this.payment.dialog = true
        this.payment.title = this.$t('Make the payment')
        this.payment.text = this.$t('We have everything needed to publish your profile')
        this.payment.status = 'no_payment'
      },
      submitProfile: function (form) {
        this.error = false
        this.loading = true
        api.updateSegment(this.id, form).then(result => {
          this.$store.commit(UPDATE_SEGMENTS, result)
          this.loading = false
          this.snackbar = true
          this.afterSubmitAlerts()
        })
      },
      switchToPortfolioTab: function () {
        this.profile_tab = 1
        this.plans_comparison = false
      },
      afterSubmitAlerts: function () {
        if (this.basicFormIsComplete) {
          if (!this.offers_basic_plan && this.info.status !== 'active') {
            this.initPaymentDialog()
          } else if (this.offers_basic_plan && this.info.status === 'file_sent') {
            this.showPlansComparison();
          }
        }
      },
      basicPlanPublish: function () {
        this.error = false
        this.loading = true
        api.basicPlanPublish(this.id).then(result => {
          this.$store.commit(UPDATE_SEGMENTS, result)
          this.loading = false
          this.snackbar = true
          this.plans_comparison = false;
        })
      },
      payMembership: function () {
        this.payment.dialog = false
        this.makePayment(
          this.info['payment_info']['amount'],
          {
            finality: 'membership',
            type: this.info['payment_info']['type'],
            post_id: this.id,
            segment: this.info['segment'],
            user_id: this.$store.state.user.id
          },
          this.info['payment_info']['type'] === 'annually'
            ? 'boleto, credit_card'
            : 'credit_card',
          this.info['payment_info']['installments']
        ).then(result => {
          console.log(result)
          this.payment.dialog = true
          this.payment.title = result.title
          this.payment.text = result.text
          this.payment.status = result.status
          if (result.status === 'paid') {
            this.payment.url = this.info['permalink']
            this.plans_comparison = false
          } else {
            this.payment.url = result.url
          }
        })
      }
    },
    created () {
      this.offers_basic_plan = this.$store.state.segments
        .find(el => el.data.id === this.id).offers_basic_plan
    },
    mounted() {
      if (this.$route.query.hasOwnProperty('upgrade') && this.showProPlanOffer) {
        this.showPlansComparison(true);
      }

      const promptPayment = !!this.$router.currentRoute.query.promptPayment;
      this.$router.replace({'query': null});
      if (promptPayment) {
        this.initPaymentDialog();
      }
    }
  }
</script>

<style lang="scss">
    .v-card__title.headline .v-icon.v-icon--left {
        margin-right: 8px;
    }
    .dashboard-profile {
        margin-bottom: 24px;
        .heading {
            grid-column-start: 1;
            grid-column-end: 3;
            text-align: left;
            h2 {
                font-size: 26px;
                color: rgba(0,0,0,0.54);
            }
            .status {
                display: block;
                font-size: 20px;
                color: rgba(0,0,0,0.87);
            }
            .url {
                display: block;
                font-size: 18px;
                color: rgba(0,0,0,0.54);
            }
        }
        h3 {
            font-size: 22px;
            color: rgba(0,0,0,0.54);
            text-align: left;
            margin-bottom: 24px;
        }
    }
    .plans_comparison_container {
        margin: 0 auto;
        max-width: 1060px;
    }
    .plans_comparison_table {
        max-width: 860px;
        border-collapse: separate;
        border: 0 solid transparent;
        border-spacing: 0;
        padding: 12px;
        margin: 24px auto;
        tr {
            line-height: 24px;
            &:not(:first-child) {
                border-top: 1px solid rgba(0,0,0,0.12);
            }
            th {
                font-size: 18px;
                padding-top: 8px;
                padding-bottom: 8px;
                &:nth-child(2) {
                    color: #000;
                    background: #FDD835;
                    border-radius: 12px 12px 0 0;
                }
            }
            td {
                text-align: center;
                padding-top: 8px;
                padding-bottom: 8px;
                border-top: 1px solid rgba(0,0,0,.12);
                &:nth-child(1) {
                    font-size: 16px;
                    text-align: left;
                    color: rgba(0,0,0,0.87);
                    padding-left: 6px;
                    padding-right: 6px;
                }
                &:not(:first-child) {
                    font-size: 18px;
                    padding-left: 18px;
                    padding-right: 18px;
                    white-space: nowrap;
                }
                &:nth-child(2) {
                    color: rgba(0,0,0,0.87);
                    position: relative;
                    &::before {
                        content: '';
                        display: block;
                        position: absolute;
                        top: -1px;
                        right: 0;
                        bottom: 0;
                        left: 0;
                        border-left: 4px solid #FDD835;
                        border-right: 4px solid #FDD835;
                    }
                }
                &:nth-child(3) {
                    color: rgba(0,0,0,0.54);
                    .material-icons {
                        color: rgba(0,0,0,0.38);
                    }
                }
            }
            &:last-child td:nth-child(2)::before {
                border-radius: 0 0 12px 12px;
                border-bottom: 4px solid #FDD835;
            }
        }
    }
</style>

<i18n>
    {
        "en": {
            "Basic": "Information",
            "Portfolio": "Portfolio",
            "Interview": "Interview",
            "Profile updated": "Profile successfully updated!",
            "Preview profile": "Preview profile",
            "Try again": "Try again",
            "Pay": "Pay",
            "View bank slip": "View bank slip",
            "Make the payment": "Make the payment",
            "We have everything needed to publish your profile": "We have everything needed to publish your profile, and you can even preview it. In order for your profile to be published, make the payment.",
            "Know more": "Know more",
            "Get more benefits with the Pro plan": "Get more benefits and get the most out of your Inspiration membership through the Pro plan",
            "Upgrade to Pro Plan": "Upgrade to Pro Plan",
            "Publish to Pro Plan": "Publish to Pro Plan",
            "Submit media to your portfolio!": "Submit media to your portfolio!",
            "At least 3 portfolio media is recommended": "We recommend that all profiles have at least 3 portfolio media, so people can see your work.",
            "Proceed anyway": "Proceed anyway",
            "Add media to my portfolio": "Add media to my portfolio",
            "You have been approved, fill the form and have your profile published!": "You have been approved, fill the form and have your profile published!",
            "Make the payment to have your profile published!": "Make the payment to have your profile published!",
            "Your profile is ready to be published": "Your profile is ready to be published",
            "Publish to Basic Plan": "Publish to Basic Plan",
            "Free": "(Free)",
            "Your payment was declined by your credit card provider": "Your payment was declined by your credit card provider",
            "Payment pending, upon confirmation, the profile will be published.": "Payment pending, upon confirmation, the profile will be published.",
            "The payment is past due. Use the button next to make a new payment": "The payment is past due. Use the button next to make a new payment",
            "Your payment has not yet been confirmed": "Your payment has not yet been confirmed. If you have already done, it will soon be confirmed. In case you haven't, make the payment by clicking the button.",
            "Your profile is publicly active": "Your profile is publicly active in the {plan} plan",
            "View profile": "View",
            "Annuity renewal pending": "Annuity renewal pending, make the payment to keep the profile published!",
            "Your profile is disabled": "Your profile is disabled, please make the payment to activate it"
        },
        "pt": {
            "Basic": "Informações",
            "Portfolio": "Portfólio",
            "Interview": "Entrevista",
            "Profile updated": "Perfil atualizado com sucesso!",
            "Preview profile": "Pré-visualizar perfil",
            "Try again": "Tentar novamente",
            "Pay": "Pagar",
            "View bank slip": "Visualizar boleto",
            "Make the payment": "Realizar o pagamento",
            "We have everything needed to publish your profile": "Estamos com todo o conteúdo necessário para publicar o seu perfil, e você pode até mesmo o pré-visualizar. Para que o seu perfil seja publicado, realize o pagamento.",
            "Know more": "Saiba mais",
            "Get more benefits with the Pro plan": "Tenha mais benefícios e tire o máximo da sua membresia Inspiration através do plano Pro",
            "Upgrade to Pro Plan": "Ativar plano Pro",
            "Publish to Pro Plan": "Publicar no Pro",
            "Submit media to your portfolio!": "Envie mídias ao seu portfolio!",
            "At least 3 portfolio media is recommended": "Recomendamos que todos perfis tenham ao menos 3 mídias de portfólio, assim as pessoas podem ver o seu trabalho.",
            "Proceed anyway": "Prosseguir assim mesmo",
            "Add media to my portfolio": "Adicionar mídias ao meu portfolio",
            "You have been approved, fill the form and have your profile published!": "Você foi aprovado, envie o formulário e tenha seu perfil publicado!",
            "Make the payment to have your profile published!": "Realize o pagamento para ter seu perfil publicado!",
            "Your profile is ready to be published": "Seu perfil está pronto pra ser publicado",
            "Publish to Basic Plan": "Publicar no Basic",
            "Free": "(gratuito)",
            "Your payment was declined by your credit card provider": "Seu pagamento foi recusado pela operadora de cartão de crédito",
            "Payment pending, upon confirmation, the profile will be published.": "Pagamento pendente. Ao ser confirmado, o perfil será publicado.",
            "The payment is past due. Use the button next to make a new payment": "O boleto do pagamento está vencido. Use o botão ao lado para realizar um novo pagamento",
            "Your payment has not yet been confirmed": "Seu pagamento ainda não foi confirmado. Caso já o realizou, em breve será confirmado. Se ainda não o fez, faça o pagamento clicando no botão ao lado.",
            "Your profile is publicly active": "Seu perfil está publicamente ativo no plano {plan}",
            "View profile": "Visualizar",
            "Annuity renewal pending": "Realize a renovação para manter seu perfil publicado!",
            "Your profile is disabled": "Seu perfil está desativado, realize o pagamento para o ativar"
        }
    }
</i18n>
